import get from 'lodash/get';

import { ActivityThemeStyles, BorderRadiusThemeProps, BorderThemeProps, FontWeight } from 'shared/common/themes/theme';
import { useWidgetStyle } from 'shared/utils/useWidgetStyle';

import { useCurrentDocument } from '../components/yjs/YjsProvider';

export enum TypographyType {
  Title = 'title',
  Headline = 'headline',
  SubHeadline = 'subHeadline',
  BodyText = 'bodyText'
}

export type TypographyIdType = keyof ActivityThemeStyles['fonts'];
export const typographyIds: TypographyIdType[] = ['title', 'headline', 'subHeadline', 'bodyText'];

export type WidgetTypographyStyle = {
  fontFamily?: string | null;
  color?: string | null;
  lineHeight?: number | null;
  letterSpacing?: number | null;
  fontSize?: number | null;
  fontWeight?: FontWeight | null;
  backgroundColor: string | null;
  borderTop: BorderThemeProps | null;
  borderRight: BorderThemeProps | null;
  borderBottom: BorderThemeProps | null;
  borderLeft: BorderThemeProps | null;
  borderRadius: BorderRadiusThemeProps | null;
  padding: number | null;
};

export type BlockTitleTypographyStyle = Omit<WidgetTypographyStyle, 'padding'> & {
  paddingTop: number | null;
  paddingBottom: number | null;
  paddingLeft: number | null;
  paddingRight: number | null;
};

export type WidgetTypographyDefaultStyle = {
  borderTop?: BorderThemeProps | null;
  borderRight?: BorderThemeProps | null;
  borderBottom?: BorderThemeProps | null;
  borderLeft?: BorderThemeProps | null;
  borderRadius?: BorderRadiusThemeProps | null;
  paddingTop?: number | null;
  paddingBottom?: number | null;
  paddingLeft?: number | null;
  paddingRight?: number | null;
  backgroundColor?: string | null;
  color?: string | null;
  width?: string | null;
  fontSize?: number | null;
};

export type SetTextWidgetStyleFunction<T> = (
  newValue: Partial<T> | undefined | ((prevState: Partial<T> | undefined) => Partial<T> | undefined)
) => void;

export type TypedWidgetStyleAndSet<T> = {
  style: Partial<T> | undefined;
  setStyle?: SetTextWidgetStyleFunction<T>;
};

export type TextWidgetStyles = Record<TypographyType, TypedWidgetStyleAndSet<WidgetTypographyStyle>>;
export type BlockTitleWidgetStyles = Record<TypographyType, TypedWidgetStyleAndSet<BlockTitleTypographyStyle>>;
export type WidgetDefaultStyles = Record<TypographyType, WidgetTypographyDefaultStyle>;

export function useTypedWidgetStyles<T>(widgetId: string): Record<TypographyIdType, TypedWidgetStyleAndSet<T>> {
  const document = useCurrentDocument();

  const [titleStyle, setTitleStyle] = useWidgetStyle<Partial<T>>(document, widgetId, TypographyType.Title);
  const [headlineStyle, setHeadlineStyle] = useWidgetStyle<Partial<T>>(document, widgetId, TypographyType.Headline);
  const [subHeadlineStyle, setSubHeadlineStyle] = useWidgetStyle<Partial<T>>(
    document,
    widgetId,
    TypographyType.SubHeadline
  );
  const [bodyTextStyle, setBodyTextStyle] = useWidgetStyle<Partial<T>>(document, widgetId, TypographyType.BodyText);

  return {
    title: { style: titleStyle, setStyle: setTitleStyle },
    headline: { style: headlineStyle, setStyle: setHeadlineStyle },
    subHeadline: { style: subHeadlineStyle, setStyle: setSubHeadlineStyle },
    bodyText: { style: bodyTextStyle, setStyle: setBodyTextStyle }
  };
}

export function useStaticTypedWidgetStyles<T>(
  widgetId: string,
  documentData: Record<string, unknown>
): Record<TypographyIdType, TypedWidgetStyleAndSet<T>> {
  const titleStyle = get(documentData, `staticContent.contentObjects.${widgetId}.style.${TypographyType.Title}`) as
    | Partial<T>
    | undefined;
  const headlineStyle = get(
    documentData,
    `staticContent.contentObjects.${widgetId}.style.${TypographyType.Headline}`
  ) as Partial<T> | undefined;
  const subHeadlineStyle = get(
    documentData,
    `staticContent.contentObjects.${widgetId}.style.${TypographyType.SubHeadline}`
  ) as Partial<T> | undefined;
  const bodyTextStyle = get(
    documentData,
    `staticContent.contentObjects.${widgetId}.style.${TypographyType.BodyText}`
  ) as Partial<T> | undefined;

  return {
    title: { style: titleStyle },
    headline: { style: headlineStyle },
    subHeadline: { style: subHeadlineStyle },
    bodyText: { style: bodyTextStyle }
  };
}

export const useBlockTitleWidgetStyles = useTypedWidgetStyles<BlockTitleTypographyStyle>;
export const useStaticBlockTitleWidgetStyles = useStaticTypedWidgetStyles<BlockTitleTypographyStyle>;
export const useTextWidgetStyles = useTypedWidgetStyles<WidgetTypographyStyle>;
export const useStaticTextWidgetStyles = useStaticTypedWidgetStyles<WidgetTypographyStyle>;
